import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import secureLocalStorage from "react-secure-storage";

import HandleSearch from "../../../utils/CommonSearchComponent";
import { GlobalStateContext } from "../../../utils/GlobalStateContext";

import { fetchAuditsThunk } from "../../../store/thunks/auditsThunk";

import SearchInput from "../SearchInput";
import media from "../../../media";
import { fetchAuditFormsThunk } from "../../../store/thunks/auditFormsThunk";
import { fetchDeletedAuditsThunk } from "../../../store/thunks/deletedAuditsThunk";
import { fetchClientsThunk } from "../../../store/thunks/clientsThunk";
import { fetchProcessesThunk } from "../../../store/thunks/processesThunk";
import { fetchUsersThunk } from "../../../store/thunks/usersThunk";
import { fetchInternalErrorRcaThunk } from "../../../store/thunks/internalErrorRcaThunk";
import { fetchDepartmentsThunk } from "../../../store/thunks/departmentsThunk";
import { createAccessMap, hasPermission } from "../../../utils/permissions";
import { getSectionKeyFromRoute } from "../../../utils/Constants";

/**
 * Pages that use Redux/Thunk based search
 * Add new pages here in future
 */
const THUNK_SEARCH_PAGES = {
  auditForms: {
    thunk: fetchAuditFormsThunk,
  },
  audits: {
    thunk: fetchAuditsThunk,
  },
  "deleted-audits": {
    thunk: fetchDeletedAuditsThunk,
  },
  "escalations/internal-error-rca": {
    thunk: fetchInternalErrorRcaThunk,
  },
  departments: {
    thunk: fetchDepartmentsThunk,
  },
  processes: {
    thunk: fetchProcessesThunk,
  },
  clients: {
    thunk: fetchClientsThunk,
  },
  users: {
    thunk: fetchUsersThunk,
  },
};

const SearchBar = (props) => {
  const { loc } = props;
  const previousPath = useRef(loc.pathname);
  const dispatch = useDispatch();
  const rawAccess = useSelector((state) => state.auth.access);

  const accessMap = useMemo(() => {
    return createAccessMap(rawAccess);
  }, [rawAccess]);
  const { globalState, setGlobalState } = useContext(GlobalStateContext);

  const [searchBarShow, setSearchBarShow] = useState(false);
  const [list, setList] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [allTransactionIdsArr, setAllTransactionIdsArr] = useState([]);
  const [selectedRowsArray, setSelectedRowsArray] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });
  const [listItems, setListItems] = useState([]);
  const [showLoader, setShowLoader] = useState(true);
  const [searchField, setSearchField] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const slsRole = secureLocalStorage.getItem("role");
  const auditType = secureLocalStorage.getItem("auditType");
  const currentSectionKey = getSectionKeyFromRoute(
    loc.pathname,
    slsRole,
    auditType,
  );

  const hasSearchAccess = hasPermission(accessMap, currentSectionKey, "search");
  const handleOnChange = (e, searchField) => {
    let value = e.target.value || "";
    if (searchField === "Score") {
      value = value.replace(/%/g, "");
    }
    let searchIn = loc.pathname.replace("/", "");

    let hasSearchedValue = "";

    if (value) {
      hasSearchedValue = "yes";
      secureLocalStorage.setItem("hasSearchedValue", "yes");
      secureLocalStorage.setItem("searchKeyword", value);
    } else {
      secureLocalStorage.removeItem("hasSearchedValue");
      secureLocalStorage.removeItem("searchKeyword");
    }

    if (THUNK_SEARCH_PAGES[searchIn]) {
      const { thunk } = THUNK_SEARCH_PAGES[searchIn];
      dispatch(
        thunk({
          searchIn:
            searchIn === process.env.REACT_APP_INTERNALERRORRCA
              ? "internal-error-rca"
              : searchIn,
          callActionFrom: "searchBar",
          hasSearchedValue,
          searchKeyword: value,
          searchField,
          pagination,
        }),
      );
      return;
    }
  };

  const handleCloseSearch = () => {
    setSearchBarShow(false);
    setSearchField("");
    setSearchValue("");

    secureLocalStorage.removeItem("searchKeyword");
    secureLocalStorage.removeItem("hasSearchedValue");

    // Reload default listing
    handleOnChange(
      {
        target: {
          value: "",
        },
      },
      "",
    );
  };
  /**
   * Sync legacy search data to globalState (unchanged behavior)
   */
  useEffect(() => {
    if (Array.isArray(searchData) && searchData.length > 0) {
      setList(searchData);
      setGlobalState((prev) => ({
        ...prev,
        list: searchData,
      }));
    }
  }, [searchData]);

  useEffect(() => {
    if (previousPath.current !== loc.pathname) {
      setSearchBarShow(false);
      setSearchField("");

      previousPath.current = loc.pathname;
    }
  }, [loc.pathname]);

  useEffect(() => {
    if (!globalState?.resetSearch) return;

    setSearchBarShow(false);
    setSearchField("");
    setSearchValue("");

    secureLocalStorage.removeItem("searchKeyword");
    secureLocalStorage.removeItem("hasSearchedValue");

    handleOnChange(
      {
        target: {
          value: "",
        },
      },
      "",
    );
  }, [globalState?.resetSearch]);
  return (
    <>
      {hasSearchAccess && (
        <div className="searchBar-containter">
          <div className="align-items-center d-flex name_search">
            <div className="search-area">
              <div
                className={`searchInput ${searchBarShow ? "activeSearch" : ""}`}
              >
                <SearchInput
                  loc={loc}
                  handleOnChange={handleOnChange}
                  searchBarShow={searchBarShow}
                  searchField={searchField}
                  setSearchField={setSearchField}
                  searchValue={searchValue}
                  setSearchValue={setSearchValue}
                />
                {searchBarShow ? (
                  <i
                    className="fa fa-times searchSymb closeSearch"
                    onClick={handleCloseSearch}
                    title="Close Search"
                  />
                ) : (
                  <img
                    src={media.searchIcon.svg}
                    alt="Search"
                    className="searchSymb"
                    onClick={() => setSearchBarShow(!searchBarShow)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SearchBar;
